/**
 * 桥接文件：让原HTML代码可以与React组件通信
 * 
 * 这个文件需要在原HTML脚本加载后执行，或者注入到原代码中
 */

import { compressImage as compressImageUtil } from '../../utils/imageCompression'

/**
 * 检查是否在 Tauri 环境中
 */
function isTauriEnvironment(): boolean {
  return typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined
}

/**
 * 获取 Tauri API
 */
function getTauriApi(): any {
  if (!isTauriEnvironment()) return null
  return (window as any).__TAURI__
}

/**
 * 注入桥接函数到window对象
 * 这个函数应该在浏览器环境中调用（在App组件中）
 */
export async function injectBridgeFunctions() {
  // 确保在浏览器环境中执行
  if (typeof window === 'undefined' || !window) {
    console.warn('⚠️ window对象不存在，无法注入桥接函数')
    return
  }

  try {
    const win = window as any
    
    // 在 Tauri 环境中，预先加载 API 模块并注入到 window 对象
    if (isTauriEnvironment()) {
      try {
        // 动态导入 Tauri API 模块
        const dialogModule = await import('@tauri-apps/api/dialog')
        const fsModule = await import('@tauri-apps/api/fs')
        const shellModule = await import('@tauri-apps/api/shell')
        
        // 将模块注入到 window.__TAURI__ 对象中（如果不存在）
        const tauri = getTauriApi()
        if (tauri) {
          if (!tauri.dialog && dialogModule) {
            tauri.dialog = dialogModule
          }
          if (!tauri.fs && fsModule) {
            tauri.fs = fsModule
          }
          if (!tauri.shell && shellModule) {
            tauri.shell = shellModule
          }
        }
        
        console.log('✅ Tauri API 模块已预加载')
      } catch (importError) {
        console.warn('⚠️ 预加载 Tauri API 模块失败（可能在生产环境中不可用）:', importError)
      }
    }

    // 全局函数：打开图片查看器
    // 在原HTML代码中调用：window.openImageViewer(imageSrc)
    win.openImageViewer = function(imageSrc: string | string[], index: number = 0) {
      const images = Array.isArray(imageSrc) ? imageSrc : [imageSrc]
      
      // 触发CustomEvent，React组件会监听这个事件
      if (typeof window !== 'undefined' && window) {
        window.dispatchEvent(new CustomEvent('openImageViewer', {
          detail: {
            images,
            index
          }
        }))
      }
    }

    // 全局函数：打开文件预览器
    win.openFileViewer = function(fileUrl: string, fileName: string) {
      if (typeof window !== 'undefined' && window) {
        window.dispatchEvent(new CustomEvent('openFileViewer', {
          detail: {
            fileUrl,
            fileName
          }
        }))
      }
    }

    // 全局函数：打开统一的对象查看器（支持图片、文本、文件）
    // 参数：itemIndex - 项目索引，从0开始
    win.openItemViewer = function(itemIndex: number) {
      console.log('🔍 openItemViewer 被调用，itemIndex:', itemIndex)
      if (typeof window !== 'undefined' && window) {
        console.log('📤 触发 openItemViewer 事件')
        window.dispatchEvent(new CustomEvent('openItemViewer', {
          detail: {
            itemIndex
          }
        }))
      } else {
        console.error('❌ window 对象不存在')
      }
    }

    // 全局函数：显示React对话框
    win.showReactDialog = function(type: string, data: any) {
      if (typeof window !== 'undefined' && window) {
        window.dispatchEvent(new CustomEvent('showReactDialog', {
          detail: {
            type,
            data
          }
        }))
      }
    }

    // 全局函数：显示画布图片右键菜单（React版本）
    // 在原HTML代码中调用：window.showCanvasImageContextMenu(itemIndex, x, y)
    // 注意：不能使用 showImageContextMenu，因为原生JS中已有同名函数
    win.showCanvasImageContextMenu = function(itemIndex: number, x: number, y: number) {
      if (typeof window !== 'undefined' && window) {
        window.dispatchEvent(new CustomEvent('showImageContextMenu', {
          detail: { itemIndex, x, y }
        }))
      }
    }

    // 全局函数：将Item送到最底层
    // 在原HTML代码中调用：await window.sendItemToBack(itemIndex)
    win.sendItemToBack = async function(itemIndex: number): Promise<boolean> {
      try {
        const win = window as any
        if (typeof win.getCurrentProject !== 'function') return false
        
        const currentProject = win.getCurrentProject()
        if (!currentProject || !currentProject.items || itemIndex < 0 || itemIndex >= currentProject.items.length) {
          return false
        }

        const item = currentProject.items[itemIndex]
        if (!item) return false

        // 找到所有items的最小zIndex
        let minZIndex = Infinity
        currentProject.items.forEach((it: any) => {
          if (it.zIndex !== undefined && it.zIndex < minZIndex) {
            minZIndex = it.zIndex
          }
        })

        // 设置当前item的zIndex为最小zIndex - 1
        item.zIndex = minZIndex === Infinity ? 0 : minZIndex - 1
        item.updatedAt = new Date().toISOString()

        // 更新DOM元素的zIndex
        const itemElement = document.querySelector(`[data-item-index="${itemIndex}"]`)
        if (itemElement) {
          ;(itemElement as HTMLElement).style.zIndex = item.zIndex.toString()
        }

        // 重新渲染画布
        if (typeof win.renderCanvas === 'function') {
          win.renderCanvas()
        }

        // 保存数据
        if (typeof win.saveData === 'function') {
          await win.saveData()
        }

        return true
      } catch (error) {
        console.error('❌ sendItemToBack 失败:', error)
        return false
      }
    }

    // 全局函数：将Item送到最高层
    // 在原HTML代码中调用：await window.sendItemToFront(itemIndex)
    win.sendItemToFront = async function(itemIndex: number): Promise<boolean> {
      try {
        const win = window as any
        if (typeof win.getCurrentProject !== 'function') return false
        
        const currentProject = win.getCurrentProject()
        if (!currentProject || !currentProject.items || itemIndex < 0 || itemIndex >= currentProject.items.length) {
          return false
        }

        const item = currentProject.items[itemIndex]
        if (!item) return false

        // 找到所有items的最大zIndex
        let maxZIndex = -Infinity
        currentProject.items.forEach((it: any) => {
          if (it.zIndex !== undefined && it.zIndex > maxZIndex) {
            maxZIndex = it.zIndex
          }
        })

        // 设置当前item的zIndex为最大zIndex + 1
        item.zIndex = maxZIndex === -Infinity ? 1000 : maxZIndex + 1
        item.updatedAt = new Date().toISOString()

        // 更新DOM元素的zIndex
        const itemElement = document.querySelector(`[data-item-index="${itemIndex}"]`)
        if (itemElement) {
          ;(itemElement as HTMLElement).style.zIndex = item.zIndex.toString()
        }

        // 重新渲染画布
        if (typeof win.renderCanvas === 'function') {
          win.renderCanvas()
        }

        // 保存数据
        if (typeof win.saveData === 'function') {
          await win.saveData()
        }

        return true
      } catch (error) {
        console.error('❌ sendItemToFront 失败:', error)
        return false
      }
    }

    // 全局函数：旋转Item
    // 在原HTML代码中调用：await window.rotateItem(itemIndex, angle)
    win.rotateItem = async function(itemIndex: number, angle: number = 90): Promise<boolean> {
      try {
        const win = window as any
        if (typeof win.getCurrentProject !== 'function') return false
        
        const currentProject = win.getCurrentProject()
        if (!currentProject || !currentProject.items || itemIndex < 0 || itemIndex >= currentProject.items.length) {
          return false
        }

        const item = currentProject.items[itemIndex]
        if (!item) return false

        // 获取旧的旋转角度
        const oldRotation = item.rotation || 0
        const newRotation = (oldRotation + angle) % 360

        // 判断是否需要交换宽高
        const oldIsVertical = (oldRotation === 90 || oldRotation === 270)
        const newIsVertical = (newRotation === 90 || newRotation === 270)

        if (oldIsVertical !== newIsVertical && item.width && item.height) {
          // 交换宽高
          const tempWidth = item.width
          item.width = item.height
          item.height = tempWidth

          // 如果是图片类型，也交换图片尺寸
          if (item.type === 'image' && item.imageWidth && item.imageHeight) {
            const tempImgWidth = item.imageWidth
            item.imageWidth = item.imageHeight
            item.imageHeight = tempImgWidth
          }
        }

        // 更新旋转信息
        item.rotation = newRotation
        item.updatedAt = new Date().toISOString()

        // 重新渲染画布
        if (typeof win.renderCanvas === 'function') {
          win.renderCanvas()
        }

        // 保存数据
        if (typeof win.saveData === 'function') {
          await win.saveData()
        }

        return true
      } catch (error) {
        console.error('❌ rotateItem 失败:', error)
        return false
      }
    }

    // 全局函数：下载图片
    // 在原HTML代码中调用：await window.downloadImage(itemIndex)
    win.downloadImage = async function(itemIndex: number): Promise<boolean> {
      try {
        const win = window as any
        if (typeof win.getCurrentProject !== 'function') return false
        
        const currentProject = win.getCurrentProject()
        if (!currentProject || !currentProject.items || itemIndex < 0 || itemIndex >= currentProject.items.length) {
          return false
        }

        const item = currentProject.items[itemIndex]
        if (!item || item.type !== 'image') return false

        // 统一使用fileId，兼容旧数据的imageId
        const fileId = item.fileId || item.imageId
        if (!fileId) {
          console.error('图片没有fileId或imageId')
          return false
        }

        // 从IndexedDB加载图片
        if (typeof win.loadMediaFromIndexedDB === 'function') {
          try {
            const mediaData = await win.loadMediaFromIndexedDB(fileId)
            if (mediaData && mediaData.url) {
              // 创建下载链接
              const a = document.createElement('a')
              a.href = mediaData.url
              a.download = item.fileName || mediaData.fileName || `image-${fileId}.${mediaData.ext || 'png'}`
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              
              // 延迟清理 Object URL
              setTimeout(() => {
                if (mediaData.url && mediaData.url.startsWith('blob:')) {
                  URL.revokeObjectURL(mediaData.url)
                }
              }, 100)
              
              return true
            }
          } catch (e) {
            console.error('从IndexedDB加载图片失败:', e)
          }
        }

        // 如果IndexedDB加载失败，尝试使用imageSrc
        if (item.imageSrc) {
          try {
            const response = await fetch(item.imageSrc)
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = item.fileName || `image-${fileId}.png`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
            return true
          } catch (e) {
            console.error('下载图片失败:', e)
            return false
          }
        }

        return false
      } catch (error) {
        console.error('❌ downloadImage 失败:', error)
        return false
      }
    }

    // ============== Tauri 本地文件功能 ==============
    
    /**
     * 检查是否在 Tauri 环境中
     * 在原HTML代码中调用：window.isTauriApp()
     */
    win.isTauriApp = function(): boolean {
      return isTauriEnvironment()
    }

    /**
     * 使用 Tauri dialog 选择文件，返回文件路径和 File 对象
     * 在原HTML代码中调用：await window.tauriSelectFiles(options)
     * @param options.multiple 是否允许多选
     * @param options.filters 文件过滤器 [{name: 'Images', extensions: ['png', 'jpg']}]
     * @returns {Promise<Array<{path: string, file: File}> | null>} 文件路径和File对象数组，取消返回null
     */
    win.tauriSelectFiles = async function(options: { multiple?: boolean, filters?: Array<{name: string, extensions: string[]}> } = {}): Promise<Array<{path: string, file: File}> | null> {
      const tauri = getTauriApi()
      if (!tauri) {
        console.warn('⚠️ 非 Tauri 环境，无法使用 tauriSelectFiles')
        return null
      }

      try {
        // 获取 dialog 和 fs 模块（应该已经在 injectBridgeFunctions 中预加载）
        let dialog: any = tauri.dialog
        let fs: any = tauri.fs
        
        // 如果模块不存在，尝试动态导入
        if (!dialog || !dialog.open) {
          try {
            const dialogModule = await import('@tauri-apps/api/dialog')
            dialog = dialogModule
            // 更新 tauri 对象以便后续使用
            if (tauri && !tauri.dialog) {
              tauri.dialog = dialogModule
            }
          } catch (importError) {
            console.error('❌ 无法加载 dialog 模块:', importError)
            return null
          }
        }
        
        if (!fs || !fs.readBinaryFile) {
          try {
            const fsModule = await import('@tauri-apps/api/fs')
            fs = fsModule
            // 更新 tauri 对象以便后续使用
            if (tauri && !tauri.fs) {
              tauri.fs = fsModule
            }
          } catch (importError) {
            console.error('❌ 无法加载 fs 模块:', importError)
            return null
          }
        }
        
        if (!dialog || !dialog.open || !fs || !fs.readBinaryFile) {
          console.error('❌ Tauri API 模块不可用', { 
            dialog: !!dialog, 
            dialogOpen: !!(dialog && dialog.open),
            fs: !!fs,
            fsReadBinaryFile: !!(fs && fs.readBinaryFile)
          })
          return null
        }
        
        // 打开文件选择对话框
        const selected = await dialog.open({
          multiple: options.multiple ?? true,
          filters: options.filters || [
            { name: 'All Files', extensions: ['*'] }
          ]
        })

        if (!selected || (Array.isArray(selected) && selected.length === 0)) {
          return null // 用户取消选择
        }

        const paths = Array.isArray(selected) ? selected : [selected]
        const results: Array<{path: string, file: File}> = []

        for (const filePath of paths) {
          try {
            // 读取文件内容
            const fileContent = await fs.readBinaryFile(filePath)
            
            // 从路径中提取文件名
            const fileName = filePath.split(/[/\\]/).pop() || 'unknown'
            
            // 推断 MIME 类型
            const ext = fileName.split('.').pop()?.toLowerCase() || ''
            const mimeTypes: Record<string, string> = {
              'png': 'image/png',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'gif': 'image/gif',
              'webp': 'image/webp',
              'svg': 'image/svg+xml',
              'pdf': 'application/pdf',
              'doc': 'application/msword',
              'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'xls': 'application/vnd.ms-excel',
              'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'ppt': 'application/vnd.ms-powerpoint',
              'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
              'txt': 'text/plain',
              'json': 'application/json',
              'xml': 'application/xml',
              'zip': 'application/zip',
              'rar': 'application/x-rar-compressed',
              '7z': 'application/x-7z-compressed',
              'mp3': 'audio/mpeg',
              'mp4': 'video/mp4',
              'avi': 'video/x-msvideo',
              'mov': 'video/quicktime'
            }
            const mimeType = mimeTypes[ext] || 'application/octet-stream'
            
            // 创建 File 对象
            const blob = new Blob([fileContent], { type: mimeType })
            const file = new File([blob], fileName, { type: mimeType })
            
            results.push({ path: filePath, file })
          } catch (err) {
            console.error(`读取文件失败: ${filePath}`, err)
          }
        }

        return results.length > 0 ? results : null
      } catch (error) {
        console.error('❌ tauriSelectFiles 失败:', error)
        return null
      }
    }

    /**
     * 检查本地文件是否存在
     * 在原HTML代码中调用：await window.tauriCheckFileExists(filePath)
     * @param filePath 文件的绝对路径
     * @returns {Promise<boolean>} 文件是否存在
     */
    win.tauriCheckFileExists = async function(filePath: string): Promise<boolean> {
      const tauri = getTauriApi()
      if (!tauri || !filePath) {
        return false
      }

      try {
        const { fs } = tauri
        return await fs.exists(filePath)
      } catch (error) {
        console.error('❌ tauriCheckFileExists 失败:', error)
        return false
      }
    }

    /**
     * 使用系统默认应用打开本地文件
     * 在原HTML代码中调用：await window.tauriOpenFile(filePath)
     * @param filePath 文件的绝对路径
     * @returns {Promise<boolean>} 是否成功打开
     */
    win.tauriOpenFile = async function(filePath: string): Promise<boolean> {
      const tauri = getTauriApi()
      if (!tauri || !filePath) {
        console.warn('⚠️ 非 Tauri 环境或路径为空，无法打开文件')
        return false
      }

      console.log('[tauriOpenFile] 尝试打开文件:', filePath)

      try {
        // 方法1：尝试将本地路径转换为 file:// URL 格式，使用 shell.open
        // Windows 路径格式：D:\path\to\file -> file:///D:/path/to/file
        try {
          const { shell } = tauri
          if (shell && shell.open) {
            // 将 Windows 路径转换为 file:// URL
            let fileUrl = filePath
            // 如果是 Windows 绝对路径（如 D:\path\to\file）
            if (/^[A-Za-z]:\\/.test(filePath)) {
              // 转换为 file:///D:/path/to/file 格式
              fileUrl = 'file:///' + filePath.replace(/\\/g, '/')
            }
            // 如果已经是相对路径或其他格式，尝试直接转换
            else if (!filePath.startsWith('file://')) {
              fileUrl = 'file:///' + filePath.replace(/\\/g, '/')
            }
            
            console.log('[tauriOpenFile] 尝试使用 shell.open 打开 file:// URL:', fileUrl)
            await shell.open(fileUrl)
            console.log('[tauriOpenFile] shell.open 成功')
            return true
          }
        } catch (shellError) {
          console.warn('[tauriOpenFile] shell.open 失败，尝试使用 invoke 命令:', shellError)
          // 如果 shell.open 失败，降级到 invoke 命令
        }

        // 方法2：使用 Tauri 命令系统打开本地文件（作为降级方案）
        let invokeFn: any = null
        
        // 方式1：从预加载的全局对象获取
        if ((window as any).__TAURI_INVOKE__ && typeof (window as any).__TAURI_INVOKE__ === 'function') {
          invokeFn = (window as any).__TAURI_INVOKE__
          console.log('[tauriOpenFile] 使用预加载的 invoke 函数')
        }
        // 方式2：从 tauri 对象获取
        else if (tauri.invoke && typeof tauri.invoke === 'function') {
          invokeFn = tauri.invoke
          console.log('[tauriOpenFile] 从 tauri 对象获取 invoke')
        }
        // 方式3：动态导入
        else {
          try {
            const tauriModule = await import('@tauri-apps/api/tauri')
            invokeFn = tauriModule.invoke
            console.log('[tauriOpenFile] 成功动态导入 invoke 函数')
          } catch (importErr) {
            console.warn('[tauriOpenFile] 动态导入失败:', importErr)
          }
        }
        
        if (invokeFn && typeof invokeFn === 'function') {
          console.log('[tauriOpenFile] 使用 invoke 命令打开文件:', filePath)
          try {
            await invokeFn('open_local_file', { filePath })
            console.log('[tauriOpenFile] 命令执行成功')
            return true
          } catch (invokeError) {
            console.error('[tauriOpenFile] invoke 命令执行失败:', invokeError)
            throw invokeError
          }
        } else {
          const errorMsg = '无法找到 invoke 函数，请检查 Tauri API 是否正确加载。可用的方法: ' + JSON.stringify({
            hasTauriInvoke: !!(window as any).__TAURI_INVOKE__,
            hasTauriInvokeInTauri: !!tauri.invoke,
            tauriKeys: Object.keys(tauri)
          })
          console.error('[tauriOpenFile]', errorMsg)
          throw new Error(errorMsg)
        }
      } catch (error) {
        console.error('❌ tauriOpenFile 失败:', error)
        return false
      }
    }

    /**
     * 压缩图片
     * 在原HTML代码中调用：await window.compressImage(file, maxSizeMB)
     * @param file 原始图片文件
     * @param maxSizeMB 压缩阈值（MB），如果文件小于此值，返回原文件
     * @returns {Promise<File>} 压缩后的文件
     */
    win.compressImage = async function(file: File, maxSizeMB: number): Promise<File> {
      try {
        return await compressImageUtil(file, maxSizeMB)
      } catch (error) {
        console.error('图片压缩失败:', error)
        // 压缩失败时返回原文件
        return file
      }
    }

    /**
     * 保存文件到本地（让用户选择保存位置）
     * 在原HTML代码中调用：await window.tauriSaveFile(blob, defaultFileName)
     * @param blob 文件 Blob 对象
     * @param defaultFileName 默认文件名
     * @returns {Promise<string | null>} 保存的文件路径，取消返回null
     */
    win.tauriSaveFile = async function(blob: Blob, defaultFileName: string): Promise<string | null> {
      const tauri = getTauriApi()
      if (!tauri) {
        console.warn('⚠️ 非 Tauri 环境，无法使用 tauriSaveFile')
        return null
      }

      try {
        const { dialog, fs } = tauri
        
        // 打开保存对话框
        const filePath = await dialog.save({
          defaultPath: defaultFileName,
          filters: [{ name: 'All Files', extensions: ['*'] }]
        })

        if (!filePath) {
          return null // 用户取消
        }

        // 将 Blob 转换为 ArrayBuffer
        const arrayBuffer = await blob.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)

        // 写入文件
        await fs.writeBinaryFile(filePath, uint8Array)

        return filePath
      } catch (error) {
        console.error('❌ tauriSaveFile 失败:', error)
        return null
      }
    }

    /**
     * 在文件管理器中显示文件
     * 在原HTML代码中调用：await window.tauriShowInFolder(filePath)
     * @param filePath 文件的绝对路径
     * @returns {Promise<boolean>} 是否成功
     */
    win.tauriShowInFolder = async function(filePath: string): Promise<boolean> {
      const tauri = getTauriApi()
      if (!tauri || !filePath) {
        return false
      }

      try {
        const { shell } = tauri
        // 获取文件所在目录
        const dirPath = filePath.replace(/[/\\][^/\\]*$/, '')
        await shell.open(dirPath)
        return true
      } catch (error) {
        console.error('❌ tauriShowInFolder 失败:', error)
        return false
      }
    }

    // ============== OSS 存储功能 ==============
    
    /**
     * 上传文件到 OSS（如果启用）
     * 在原HTML代码中调用：await window.uploadFileToOSS(blob, objectKey)
     * @param blob 文件 Blob 对象
     * @param objectKey OSS 对象键（路径）
     * @returns {Promise<{url: string, fileId: string} | null>} 上传结果，失败返回null
     */
    win.uploadFileToOSS = async function(blob: Blob, objectKey: string): Promise<{url: string, fileId: string} | null> {
      try {
        // 动态导入 OSS 服务
        const { uploadFileToOSS } = await import('../../services/ossService')
        return await uploadFileToOSS(blob, objectKey)
      } catch (error) {
        console.error('❌ uploadFileToOSS 失败:', error)
        return null
      }
    }

    /**
     * 获取存储配置
     * 在原HTML代码中调用：await window.getStorageConfig()
     * @returns {Promise<{useOSS: boolean, bucket: string | null, region: string | null, endpoint: string | null} | null>}
     */
    win.getStorageConfig = async function() {
      try {
        console.log('📦 [bridge] getStorageConfig 被调用')
        const { useStorageConfigStore } = await import('../../store/useStorageConfigStore')
        const store = useStorageConfigStore.getState()
        const config = store.config
        console.log('📦 [bridge] 返回存储配置:', config)
        
        // 如果配置未初始化，尝试初始化
        if (!store.isInitialized) {
          console.log('📦 [bridge] 配置未初始化，开始初始化...')
          await store.initConfig()
          return store.config
        }
        
        return config
      } catch (error) {
        console.error('❌ getStorageConfig 失败:', error)
        return null
      }
    }

    /**
     * 生成云存储文件 URL
     * 在原HTML代码中调用：window.getOssFileUrl(objectKey, bucket, region, endpoint, provider)
     * @param objectKey 对象键
     * @param bucket Bucket 名称
     * @param region 区域
     * @param endpoint 端点（可选，仅用于自定义域名）
     * @param provider 云服务商类型：'aliyun' 或 'tencent'（可选，默认 'aliyun'）
     * @returns {string} 云存储文件 URL
     */
    win.getOssFileUrl = function(objectKey: string, bucket: string, region: string, endpoint?: string, provider?: 'aliyun' | 'tencent'): string {
      const cloudProvider = provider || 'aliyun'
      
      // 腾讯云 COS
      if (cloudProvider === 'tencent') {
        // 腾讯云 COS URL 格式: https://{bucket}.cos.{region}.myqcloud.com/{objectKey}
        // 注意：腾讯云的 endpoint 通常是通用格式（如 https://cos.ap-chengdu.myqcloud.com），
        // 不包含 bucket，所以需要使用标准的 bucket URL 格式
        return `https://${bucket}.cos.${region}.myqcloud.com/${objectKey}`
      }
      
      // 阿里云 OSS
      if (endpoint && !endpoint.includes('aliyuncs.com')) {
        // 如果提供了自定义域名（非阿里云标准域名），直接使用
        return `${endpoint}/${objectKey}`
      }
      // 阿里云 OSS URL 格式: https://{bucket}.oss-{region}.aliyuncs.com/{objectKey}
      return `https://${bucket}.oss-${region}.aliyuncs.com/${objectKey}`
    }

    /**
     * 生成文件对象键
     * 在原HTML代码中调用：window.generateObjectKey(userUuid, projectId, fileId)
     * @param userUuid 用户 UUID
     * @param projectId 项目 ID
     * @param fileId 文件 ID
     * @returns {string} OSS 对象键
     */
    win.generateObjectKey = function(userUuid: string, projectId: string, fileId: string): string {
      return `${userUuid}/${projectId}/${fileId}`
    }

    // 全局函数：打开集市页面
    win.openMarketplace = function() {
      if (typeof window !== 'undefined' && window) {
        // 使用 window.location.href 跳转，或者触发 React 路由跳转
        // 由于是 SPA，我们尽量用 history.pushState 或触发 CustomEvent
        // 但为了简单且确保路由生效，直接修改 href 也可以（会刷新），或者使用 bridge event
        window.history.pushState({}, '', '/marketplace')
        // 触发 popstate 让 React Router 感知
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    }

    // 全局函数：导入项目数据
    win.importProjectFromData = async function(projectData: any) {
        // 这里只是个占位，真正的逻辑在 store 或 main logic 中
        // 但 legacy-script.js 可能有 importProject 逻辑
        // 我们也可以在这里直接调用 legacy-script 的 loadProject
        const win = window as any
        if (win.loadProjectData) {
            await win.loadProjectData(projectData)
        }
    }

    // 显示项目上传对话框
    win.showProjectUploadDialog = function() {
      console.log('🔔 [bridge] showProjectUploadDialog 被调用')
      if (typeof window !== 'undefined' && window) {
        console.log('📤 [bridge] 触发 showProjectUploadDialog 事件')
        window.dispatchEvent(new CustomEvent('showProjectUploadDialog'))
      } else {
        console.error('❌ [bridge] window 对象不存在')
      }
    }

    // ============== 存储服务函数 ==============
    
    // 注入媒体存储服务函数
    try {
      const storageModule = await import('../../services/storage/index')
      
      // 上传单个媒体文件
      win.uploadMediaToServerTS = async function(userUuid: string, projectId: string, fileId: string) {
        return storageModule.uploadMediaToServer(userUuid, projectId, fileId)
      }
      
      // 上传项目所有媒体文件
      win.uploadProjectMediaToServerTS = async function(userUuid: string, project: any, onProgress?: (current: number, total: number, fileId: string) => void) {
        return storageModule.uploadProjectMediaToServer(userUuid, project, onProgress)
      }
      
      // 推送项目到服务器（包含媒体上传）
      win.pushProjectToServerTS = async function(userUuid: string, project: any, onProgress?: (stage: string, message: string) => void) {
        return storageModule.pushProjectToServer(userUuid, project, onProgress)
      }
      
      // 检查文件是否已上传
      win.isFileUploadedTS = async function(userUuid: string, projectId: string, fileId: string) {
        return storageModule.isFileUploaded(userUuid, projectId, fileId)
      }
      
      // 标记文件为已上传
      win.markFileAsUploadedTS = async function(userUuid: string, projectId: string, fileId: string, storageProvider: string) {
        return storageModule.markFileAsUploaded(userUuid, projectId, fileId, storageProvider)
      }
      
      // 提取项目媒体引用
      win.extractProjectMediaReferencesTS = function(project: any) {
        return storageModule.extractProjectMediaReferences(project)
      }
      
      console.log('📦 [bridge] 存储服务函数已注入')
    } catch (storageError) {
      console.warn('⚠️ [bridge] 存储服务函数注入失败:', storageError)
    }

    console.log('✅ 桥接函数已注入')
    console.log('📦 [bridge] OSS相关函数注入状态:', {
      uploadFileToOSS: typeof win.uploadFileToOSS === 'function',
      getStorageConfig: typeof win.getStorageConfig === 'function',
      getOssFileUrl: typeof win.getOssFileUrl === 'function',
      generateObjectKey: typeof win.generateObjectKey === 'function'
    })
    if (isTauriEnvironment()) {
      console.log('✅ Tauri 环境检测成功，本地文件功能可用')
    }
  } catch (error) {
    console.error('❌ 注入桥接函数失败:', error)
  }
}

// 导出类型（供TypeScript使用）
export interface ImageViewerEvent {
  images: string[]
  index: number
}

export interface FileViewerEvent {
  fileUrl: string
  fileName: string
}

// 确保桥接函数在全局可用
if (typeof window !== 'undefined') {
  console.log('✅ 桥接函数已注入')
}
